import React, { ReactNode } from "react";
import {
  CmsBlock,
  CmsText,
  CmsImage,
  CmsGallery,
  CmsYoutubeVideo,
  CmsLayout,
  CmsSeparator,
} from "../types/cms";
import Alignable from "../types/alignable";
import Text from "./content/Text";
import Image from "./content/Image";
import Gallery from "./content/Gallery";
import Youtube from "./content/Youtube";
import Layout from "./content/Layout";
import Separator from "./content/Separator";

type ContentPropsType = {
  data: CmsBlock | CmsBlock[];
  depth: number | undefined;
  onDark?: boolean;
  preview?: boolean;
};

const Content: React.FC<ContentPropsType & Alignable> = ({
  data,
  depth = 0,
  align,
  onDark = false,
  preview = false,
}) => {
  if (!data) {
    return null;
  }

  if (Array.isArray(data)) {
    return data.map((content, i) => (
      <Content
        key={`field-${i}`}
        data={content}
        depth={depth}
        align={align}
        onDark={onDark}
        preview={preview}
      />
    ));
  }

  let heading: ReactNode = null;

  if (data.heading) {
    const className = `font-headline text-headline text-light-headline`;
    const sizes = ["text-2xl", "text-xl", "text-lg", "text-base"];
    const size = sizes[Math.min(depth, sizes.length - 1)];
    const HeadingTag = `h${Math.min(
      depth + 2,
      6
    )}` as keyof JSX.IntrinsicElements;

    heading = (
      <HeadingTag className={`${size} ${className}`}>{data.heading}</HeadingTag>
    );
  }

  const baseProps = {
    heading,
    align,
    onDark,
    preview,
    depth,
  };

  switch (data.type) {
    case "text":
      return <Text {...baseProps} data={data as CmsText} />;
    case "image":
      return <Image {...baseProps} data={data as CmsImage} />;
    case "gallery":
      return <Gallery {...baseProps} data={data as CmsGallery} />;
    case "youtube":
      return <Youtube {...baseProps} data={data as CmsYoutubeVideo} />;
    case "layout":
      return <Layout {...baseProps} data={data as CmsLayout} />;
    case "separator":
      return <Separator {...baseProps} data={data as CmsSeparator} />;
    default: {
      const exhaustiveCheck: never = data;
      console.error(
        `Unhandled content type: ${(exhaustiveCheck as any).type}. Data:`,
        exhaustiveCheck
      );
      return null;
    }
  }
};

export default Content;
